import { useActionForm } from "@gadgetinc/react";
import { api } from "../api";
import { useSearchParams, Link } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";

const calculatePasswordStrength = (password) => {
  if (!password) return { score: 0, label: "None", color: "#e0e0e0" };
  
  let score = 0;
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };
  
  Object.values(checks).forEach(check => { if (check) score++; });
  
  if (score <= 2) return { score, label: "Weak", color: "#ef4444" };
  if (score <= 4) return { score, label: "Medium", color: "#f59e0b" };
  return { score, label: "Strong", color: "#10b981" };
};

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const [paramError, setParamError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const token = searchParams.get("token");
  const code = searchParams.get("code");
  const resetParam = token || code;
  
  const {
    submit,
    register,
    watch,
    formState: { errors, isSubmitSuccessful, isSubmitting },
  } = useActionForm(api.user.resetPassword, {
    defaultValues: { code: resetParam },
  });

  const password = watch("password");
  const confirmPassword = watch("confirmPassword");
  
  const passwordStrength = useMemo(() => calculatePasswordStrength(password), [password]);
  
  const passwordRequirements = useMemo(() => [
    { label: "At least 8 characters", met: password?.length >= 8 },
    { label: "Contains uppercase letter (A-Z)", met: /[A-Z]/.test(password || "") },
    { label: "Contains lowercase letter (a-z)", met: /[a-z]/.test(password || "") },
    { label: "Contains number (0-9)", met: /[0-9]/.test(password || "") },
    { label: "Contains special character", met: /[^A-Za-z0-9]/.test(password || "") },
  ], [password]);

  useEffect(() => {
    if (!resetParam) {
      setParamError("Missing reset token. Please check your email link or request a new one.");
    } else {
      setParamError(null);
    }
  }, [resetParam]);

  if (paramError) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card error-card">
          <div className="reset-password-header">
            <h1 className="brand-title">Elevation EMS</h1>
            <h2 className="page-subtitle">Reset Your Password</h2>
          </div>
          <div className="error-content">
            <div className="error-icon">⚠️</div>
            <p className="error-message">{paramError}</p>
            <div className="error-actions">
              <Link to="/forgot-password" className="primary-link-button">
                Request New Reset Link
              </Link>
              <Link to="/sign-in" className="secondary-link">
                Back to Sign In
              </Link>
            </div>
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (isSubmitSuccessful) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card success-card">
          <div className="reset-password-header">
            <h1 className="brand-title">Elevation EMS</h1>
            <h2 className="page-subtitle">Password Reset Complete</h2>
          </div>
          <div className="success-content">
            <div className="success-animation">
              <div className="success-checkmark">✓</div>
            </div>
            <p className="success-message">
              Your password has been reset successfully!
            </p>
            <Link to="/sign-in" className="primary-link-button">
              Sign In Now
            </Link>
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <div className="reset-password-header">
          <h1 className="brand-title">Elevation EMS</h1>
          <h2 className="page-subtitle">Reset Your Password</h2>
          <p className="page-description">
            Enter your new password below
          </p>
        </div>

        <form className="reset-password-form" onSubmit={submit}>
          {errors?.root?.message && (
            <div className="error-banner animate-in">
              <span className="error-icon">⚠️</span>
              <span>{errors.root.message}</span>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">New Password</label>
            <div className="password-input-wrapper">
              <input
                className={`form-input ${errors?.password ? "error" : ""}`}
                placeholder="Enter new password"
                type={showPassword ? "text" : "password"}
                {...register("password", { 
                  required: "Password is required",
                  minLength: { value: 8, message: "Password must be at least 8 characters" }
                })}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
            {errors?.password?.message && (
              <p className="field-error animate-in">{errors.password.message}</p>
            )}
            {errors?.user?.password?.message && (
              <p className="field-error animate-in">{errors.user.password.message}</p>
            )}
          </div>

          {password && (
            <div className="password-strength-section animate-in">
              <div className="strength-header">
                <span className="strength-label">Password Strength:</span>
                <span className="strength-value" style={{ color: passwordStrength.color }}>
                  {passwordStrength.label}
                </span>
              </div>
              <div className="strength-bar-container">
                <div 
                  className="strength-bar" 
                  style={{ 
                    width: `${(passwordStrength.score / 5) * 100}%`,
                    backgroundColor: passwordStrength.color 
                  }}
                />
              </div>
              <div className="password-requirements">
                {passwordRequirements.map((req, index) => (
                  <div key={index} className={`requirement ${req.met ? "met" : ""}`}>
                    <span className="requirement-icon">{req.met ? "✓" : "○"}</span>
                    <span className="requirement-text">{req.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <div className="password-input-wrapper">
              <input
                className={`form-input ${errors?.confirmPassword ? "error" : ""}`}
                placeholder="Confirm new password"
                type={showConfirmPassword ? "text" : "password"}
                {...register("confirmPassword", {
                  required: "Please confirm your password",
                  validate: (value) => value === password || "Passwords do not match",
                })}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
            {errors?.confirmPassword?.message && (
              <p className="field-error animate-in">{errors.confirmPassword.message}</p>
            )}
            {!errors?.confirmPassword && confirmPassword && password === confirmPassword && (
              <p className="field-success animate-in">✓ Passwords match</p>
            )}
          </div>

          <button 
            type="submit" 
            className="submit-button" 
            disabled={isSubmitting || !resetParam}
          >
            {isSubmitting ? (
              <>
                <span className="spinner" />
                <span>Resetting Password...</span>
              </>
            ) : (
              "Reset Password"
            )}
          </button>
        </form>

        <div className="reset-password-footer">
          <Link to="/sign-in" className="footer-link">
            ← Back to Sign In
          </Link>
          <span className="footer-separator">•</span>
          <Link to="/forgot-password" className="footer-link">
            Request New Link
          </Link>
        </div>
      </div>
      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .reset-password-container {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 1rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  }

  .reset-password-card {
    background: white;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    width: 100%;
    max-width: 480px;
    padding: 2.5rem;
    animation: slideUp 0.4s ease-out;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .reset-password-header {
    text-align: center;
    margin-bottom: 2rem;
  }

  .brand-title {
    font-size: 2rem;
    font-weight: 700;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0 0 0.5rem 0;
  }

  .page-subtitle {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 0.5rem 0;
  }

  .page-description {
    font-size: 0.95rem;
    color: #6b7280;
    margin: 0;
  }

  .reset-password-form {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .error-banner {
    padding: 0.875rem 1rem;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    color: #dc2626;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .error-icon {
    font-size: 1rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .form-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
  }

  .password-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  .form-input {
    width: 100%;
    padding: 0.75rem 2.5rem 0.75rem 1rem;
    font-size: 1rem;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    transition: all 0.2s;
    outline: none;
  }

  .form-input:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  .form-input.error {
    border-color: #ef4444;
  }

  .form-input.error:focus {
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
  }

  .password-toggle {
    position: absolute;
    right: 0.75rem;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.25rem;
    font-size: 1.25rem;
    opacity: 0.6;
    transition: opacity 0.2s;
  }

  .password-toggle:hover {
    opacity: 1;
  }

  .field-error {
    font-size: 0.8125rem;
    color: #ef4444;
    margin: 0;
  }

  .field-success {
    font-size: 0.8125rem;
    color: #10b981;
    margin: 0;
  }

  .password-strength-section {
    padding: 1rem;
    background: #f9fafb;
    border-radius: 8px;
    margin-top: -0.5rem;
  }

  .strength-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .strength-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #6b7280;
  }

  .strength-value {
    font-size: 0.8125rem;
    font-weight: 700;
  }

  .strength-bar-container {
    height: 6px;
    background: #e5e7eb;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 1rem;
  }

  .strength-bar {
    height: 100%;
    transition: all 0.3s ease;
    border-radius: 3px;
  }

  .password-requirements {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .requirement {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8125rem;
    color: #6b7280;
    transition: color 0.2s;
  }

  .requirement.met {
    color: #10b981;
  }

  .requirement-icon {
    font-size: 0.875rem;
    font-weight: bold;
  }

  .requirement-text {
    flex: 1;
  }

  .submit-button {
    width: 100%;
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
    font-weight: 600;
    color: white;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }

  .submit-button:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
  }

  .submit-button:active:not(:disabled) {
    transform: translateY(0);
  }

  .submit-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .reset-password-footer {
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    font-size: 0.875rem;
  }

  .footer-link {
    color: #667eea;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s;
  }

  .footer-link:hover {
    color: #764ba2;
    text-decoration: underline;
  }

  .footer-separator {
    color: #d1d5db;
  }

  .animate-in {
    animation: fadeIn 0.3s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .error-card {
    text-align: center;
  }

  .error-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }

  .error-icon {
    font-size: 3rem;
  }

  .error-message {
    font-size: 1rem;
    color: #6b7280;
    margin: 0;
  }

  .error-actions {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
  }

  .primary-link-button {
    display: inline-block;
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
    font-weight: 600;
    color: white;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 8px;
    text-decoration: none;
    transition: all 0.2s;
  }

  .primary-link-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
  }

  .secondary-link {
    color: #667eea;
    text-decoration: none;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .secondary-link:hover {
    text-decoration: underline;
  }

  .success-card {
    text-align: center;
  }

  .success-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
    margin-top: 1rem;
  }

  .success-animation {
    animation: scaleIn 0.5s ease-out;
  }

  @keyframes scaleIn {
    from {
      transform: scale(0);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  .success-checkmark {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 3rem;
    font-weight: bold;
    box-shadow: 0 10px 30px rgba(16, 185, 129, 0.3);
  }

  .success-message {
    font-size: 1.125rem;
    color: #374151;
    margin: 0;
  }

  @media (max-width: 640px) {
    .reset-password-card {
      padding: 2rem 1.5rem;
    }

    .brand-title {
      font-size: 1.75rem;
    }

    .page-subtitle {
      font-size: 1.25rem;
    }
  }
`;
